import { Tooltip as ReactTooltip } from 'react-tooltip';
import 'react-tooltip/dist/react-tooltip.css';
import { useState } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import PropTypes from 'prop-types';
import { offset } from '@floating-ui/react-dom';

import { operationItemDataType } from '../../constants';
import { insertOperationCode } from '../../store/CodePanel/actions';
import {
  RESIZER_CONTAINER_BETWEEN_OPERATIONS_MENU_AND_NON_OPERATIONS_SELECTOR,
  RESIZER_CONTAINER_BETWEEN_PREVIEW_AND_CODE_SELECTOR,
} from '../../constants/resizing-components';
import { sendGAEvent } from '../../store/GoogleAnalytics/actions';
import { CLICK_ADD_CODE_BUTTON, DRAG_OPERATION_ITEM } from '../../constants/google-analytics';

const OperationItem = ({ op, isDarkTheme, onInsertOperationCode, onSendGAEvent, groupName }) => {
  const [tooltipTimeout, setTooltipTimeout] = useState(null);
  const [showTooltip, setShowTooltip] = useState(false);
  const defaultTooltipTimeout = 250;
  const resizerContainerBetweenOperationsMenuAndNonOperations = document.querySelector(
    RESIZER_CONTAINER_BETWEEN_OPERATIONS_MENU_AND_NON_OPERATIONS_SELECTOR,
  );
  const resizerContainerBetweenPreviewAndCode = document.querySelector(
    RESIZER_CONTAINER_BETWEEN_PREVIEW_AND_CODE_SELECTOR,
  );
  const hiddenResizer = 'hidden-resizer';
  /**
   * Use `${groupName}-${op.displayName}`, replacing all spaces with dashes and to lowercase.
   */
  const tooltipId = `${groupName}-${op.displayName}`.toLowerCase().replaceAll(' ', '-');

  /**
   * We will hide and display tooltip to prevent draggable bug where if the tooltip is render/display is not none it will
   * drag all siblings of the element and parent. So we will by default not render tooltip and show it only when the info
   * icon is hovered. We need timeouts to add delays to not showing tooltip. This can not be done in css because you can not
   * set delays for "display:none". You can set delays for "visibility" and "opacity" but draggable bug will still occur.
   */
  const displayTooltip = () => {
    clearTimeout(tooltipTimeout);
    setShowTooltip(true);
    // Hide resizer when displaying tooltip to prevent overlap. By default the parent has no class so we assign one here to hide it.
    if (resizerContainerBetweenOperationsMenuAndNonOperations) {
      resizerContainerBetweenOperationsMenuAndNonOperations.className = hiddenResizer;
      resizerContainerBetweenPreviewAndCode.className = hiddenResizer;
    }
  };

  const hideTooltip = () => {
    setTooltipTimeout(
      setTimeout(() => {
        setShowTooltip(false);
        // Show resizer again once tooltip closes by removing the class
        if (resizerContainerBetweenOperationsMenuAndNonOperations) {
          resizerContainerBetweenOperationsMenuAndNonOperations.removeAttribute('class');
          resizerContainerBetweenPreviewAndCode.removeAttribute('class');
        }
      }, defaultTooltipTimeout),
    );
  };

  const generateOperationItemTooltipContent = (dataTip) => (
    <div className="tooltip">
      <div className="tooltip-text">{dataTip}</div>
      <div className="tooltip-footer">
        <div className="tooltip-link">
          <a href={op.link} target="_blank" rel="noreferrer">
            Learn More
          </a>
        </div>
        <div
          role="button"
          className="add-code"
          onMouseDown={() => {
            onSendGAEvent(CLICK_ADD_CODE_BUTTON, op.displayName);
            onInsertOperationCode(op.code, op.location);
          }}
        >
          <label htmlFor="plus-button" className="add-code__text">
            Add code
          </label>
          <span id="plus-button" className="plus-button material-icons-outlined">
            add_circle_outline
          </span>
        </div>
      </div>
    </div>
  );

  return (
    <div
      className="operation-item"
      draggable
      onDragStart={(e) => {
        // The code to be dragged
        e.dataTransfer.setData(operationItemDataType, op.code);
        e.stopPropagation();
        onSendGAEvent(DRAG_OPERATION_ITEM, op.displayName);
      }}
    >
      <div className="item">
        <span className="item__text" id={op.displayName}>
          {op.displayName}
        </span>
      </div>

      <div
        className="info"
        // onMouseDown hide tooltips for drag
        onMouseDown={() => setShowTooltip(false)}
        onMouseEnter={displayTooltip}
        onMouseLeave={hideTooltip}
      >
        <p id={tooltipId} className="information-fill" />

        {showTooltip && (
          <div
            className="react-tooltip-container"
            draggable
            onMouseDown={(e) => e.stopPropagation()}
            onDragStart={(e) => {
              e.stopPropagation();
              e.preventDefault();
            }}
          >
            <ReactTooltip
              // need to and a "#" prefix before the id for react-tooltip's `anchorSelect` field.
              anchorId={tooltipId}
              className="react-tooltip"
              place="right"
              arrowColor={isDarkTheme ? 'black' : 'white'}
              variant={isDarkTheme ? 'dark' : 'light'}
              delayHide={defaultTooltipTimeout}
              border={false}
              clickable
              multiline
              content={generateOperationItemTooltipContent(op.tooltip)}
              isOpen={showTooltip}
              style={{ position: 'fixed' }}
              middlewares={[offset(10)]}
            />
          </div>
        )}
      </div>
    </div>
  );
};

OperationItem.propTypes = {
  op: PropTypes.shape({
    displayName: PropTypes.string.isRequired,
    tooltip: PropTypes.string.isRequired,
    link: PropTypes.string.isRequired,
    location: PropTypes.string.isRequired,
    code: PropTypes.string.isRequired,
  }).isRequired,
  isDarkTheme: PropTypes.bool.isRequired,
  onInsertOperationCode: PropTypes.func.isRequired,
  onSendGAEvent: PropTypes.func.isRequired,
  groupName: PropTypes.string.isRequired,
};

function mapDispatchToProps(dispatch) {
  return bindActionCreators(
    {
      onInsertOperationCode: insertOperationCode,
      onSendGAEvent: sendGAEvent,
    },
    dispatch,
  );
}

export default connect(null, mapDispatchToProps)(OperationItem);
